import { CaseRecord } from "../models/Case"
import { Contact } from "../models/Contact"
import { Litigation } from "../models/Litigation"
import { Document } from "../models/Document"
import { Activity } from "../models/Activity"
import { Offer } from "../models/Offer"

type CaseSummary = any

function ConvertDateForDBOn(date: string | Date ) {
    return new Date(date).toISOString().split('T')[0]
}

function Today() {
    return new Date().toISOString().split('T')[0]
}

function MoneyToNumeral(value: string) {
    return !value ? 0 : Number(value.replace(/[^\d.-]/g, ''))
}

function DaysBetweenDates(date1: string, date2: string) {
    if (!date1 || !date2) return 9999999999
    return Math.abs(new Date(date2).getTime() - new Date(date1).getTime()) / (1000 * 60 * 60 * 24)
}

export function SetCaseSummaryFromContact(contact: Contact, id: string, cs: CaseSummary) {
        cs.ContactId =     id
        // cs.ClientName =    contact?.FirstName + ' ' + contact?.LastName
        cs.ClientName =    contact?.LastName + ', ' + contact?.FirstName
        cs.ContactPhone =  contact?.Phone
        cs.ContactEmail =  contact?.Email
        cs.Prefers =       contact?.CommunicationPreference
        cs.ContactDOB =    contact?.PersonalInfo?.DateOfBirth
        cs.ContactSSN =    contact?.PersonalInfo?.SSN
        cs.ContactGender = contact?.PersonalInfo?.Gender
}

export function SetCaseSummaryFromCase(c: CaseRecord, cs: any) {
  cs.Status     = c.Status
  cs.Name       = c.Name
  cs.AssignedTo = c.AssignedTo
  cs.Firm       = c.Details?.LegalRepresentation?.Firm
  cs.DateOfLoss = c.Details?.CollisionInformation?.DateOfLoss
  cs.LastUpdate = c.updatedAt ? ConvertDateForDBOn(c.updatedAt) : "None"
  cs.SOL        = c.Details?.CollisionInformation?.StatuteOfLimitations
  
  if (c.Details) {
    cs.Severity =       c.Details?.Snapshot?.Severity
    cs.PropertyDamage = c.Details?.Snapshot?.PropertyDamageTotal
    cs.DemandSent =     c.Details?.Settlement?.DemandSent
    cs.DemandValue =    c.Details?.Settlement?.DemandValue
    cs.Strike =         c.Details?.Settlement?.StrikeNumber
    cs.Settlement =     c.Details?.Settlement?.FinalSettlement
    cs.CheckReceived =  c.Details?.Settlement?.CheckReceivedDate
    cs.Invoice =        c.Details?.Settlement?.Invoice
    cs.ClientPayout =   c.Details?.Settlement?.ClientPayout
    cs.OurPayout =      c.Details?.Settlement?.OurPayout
    cs.TotalSpecials = MoneyToNumeral(c.Details?.Settlement?.MedicalSpecials) + MoneyToNumeral(c.Details?.Settlement?.WageLoss) + MoneyToNumeral(c.Details?.Settlement?.PropertyDamages)
  }    
  
  cs.Intakes = "Pending"
  // cs.LatestOffer = "Pending"
}

export function SetCaseSummaryFromOffers(offers: Offer[], cs: CaseSummary) {
  cs.Offers = offers
  cs.LatestOffer = offers?.length > 0 ? offers[offers?.length - 1]?.Amount : "None"
}


export function SetCaseSummaryFromLitigations(lits: Litigation[], cs: CaseSummary) {     
  switch (lits.length) {
      case 0: 
      cs.CaseNumber = "None"
      cs.CaseName =   "None"
      cs.CourtName =  "None"
      cs.CourtDates = "None"
      break;
      case 1:
      cs.CaseNumber = lits[0]?.CaseNumber
      cs.CaseName =   lits[0]?.Name
      cs.CourtName =  lits[0]?.CourtName
      cs.CourtDates = "Pending" //results[0].get("CaseNumber"));
      break;      
      default:
      cs.CaseNumber = "Multiple"
      cs.CaseName =   "Multiple"
      cs.CourtName =  "Multiple"
      cs.CourtDates = "Multiple"
      break   
  }  
}
    
function dateForActivity(acts: Activity[], name: string) {
  if (!acts) return ""

  const act = acts?.find(a => a.Type.startsWith(name))
  if (!act) return ""
  const d = act ? act?.On : ""

  return d
}

export function SetCSProgressAlerts(c: CaseRecord, acts: Activity[], activityMeta: any, cs: CaseSummary) {
  if (!acts || !activityMeta) return

    let progress: string[] = []
    let summary = ""
    let summaryCompleted = false
    let hasAlert = ""
    cs.ActivityAlerts = []
    
    // Missing data alerts
    let missingFields = [] as string[]
    if (!c?.Name) missingFields.push("Case Name")
    if (!c?.AssignedTo) missingFields.push("Assigned To")
    if (!c?.Status) missingFields.push("Status")
    if (!c?.Details?.CollisionInformation?.DateOfLoss) missingFields.push("Date of Loss")
    if (!c?.Details?.CollisionInformation?.StatuteOfLimitations) missingFields.push("Statute of Limitations")
    if (!c?.Details?.CollisionInformation?.TimeOfCollision) missingFields.push("Time of Injury")
    if (!c?.Details?.CollisionInformation?.Address) missingFields.push("Address of Injury")
    if (!c?.Details?.CollisionInformation?.Injuries) missingFields.push("Injuries")
    // if (!c?.Details?.ThirdPartyInsurance?.Company) missingFields.push("Third Party Insurance Company")
    // if (!c?.Details?.ThirdPartyInsurance?.ClaimNumber) missingFields.push("Third Party Claim Number")

    if (missingFields.length > 0) {
      const missingAlert = activityMeta.find(a => a.name === "Missing Fields")
      const days = DaysBetweenDates(c.createdAt ?? "", Today())
      let alert = "" // Default to no alert
      if (days > missingAlert?.warn) {alert = "warn"}
      else if (days > missingAlert?.remind) {alert = "remind"}

      progress.push("Missing fields: " + missingFields.join(", "))
      cs.ActivityAlerts.push({ name: "Missing Fields", on: Today(), alert: alert })
      if (hasAlert !== "warn") hasAlert = alert
    }

    // Last Contact special case
    const lastContact = dateForActivity(acts, "Contact")
    let alert = "" // Default to no alert
    if (!lastContact) {alert = "warn"} // If no contact, then warn
    else {
      const caseDays = DaysBetweenDates(c.createdAt ?? "", Today()) // how many days old is this Case.
      const days = DaysBetweenDates(lastContact, Today())
      if (caseDays > 30) {
        if (days > 25) {alert = "warn"}
        else if (days > 15) {alert = "remind"}
      } else {
        // For the first month, warnings are more frequent
        if (days > 7) {alert = "warn"}
        else if (days > 4) {alert = "remind"}
      }
    }
    cs.LastContact = lastContact
    cs.ActivityAlerts.push({ name: "Contact", on: lastContact, alert: alert })
    hasAlert = alert // Set the overall alert level

    // Statute of Limitations special case
    const hasLit = cs.CaseNumber !== "None"
    const statusSuppressesSOL = ["Final Settlement", "In Litigation", "Closed"]
    const hasSuppress = statusSuppressesSOL.includes(c.Status)
    if (!hasLit && !hasSuppress) { // Only check SOL if no Litigation exists or we are not in Final Settlement.
      alert = "" // Default to no alert
      const sol = c?.Details?.CollisionInformation?.StatuteOfLimitations
      if (!sol) alert = "warn" // If no SOL, then warn
      else {
        const days = DaysBetweenDates(Today(), sol)
        if (days <= 60) {alert = "warn"}
        else if (days <= 120) {alert = "remind"}
      }
      cs.ActivityAlerts.push({ name: "Statute of Limitations", on: sol, alert: alert })
      if (hasAlert !== "warn" && (alert !== "")) hasAlert = alert // Set the overall alert level, only if it escalates
    }

    // Loop through all possible Activities in order, to build the progress and alerts.
    for (const activity of activityMeta) {
      // When did Activity happen, if at all
      const on = dateForActivity(acts, activity.name)

      // Assign special alerts directly to CaseSummary values
      switch (activity.name) {
        case 'Demand Sent':           cs.DemandSent         = on; break
        case 'Statement of Exhibits': cs.SOE                = on; break
        case 'Completed Treatment':   cs.CompletedTreatment = on; break
        case 'Records Ordered':       cs.RecordsOrdered     = on; break
      }

      // Calculate the alert for this activity
      let alert = ""
      if (!on) {
        const sinceActDate = cs.ActivityAlerts.find((a: any) => a.name === activity.since)?.on ?? ""

        if (sinceActDate) {
          const days = DaysBetweenDates(sinceActDate, "") // "" defaults to today
          if (days > activity.remind) alert = 'remind'
          if (days > activity.warn) alert = 'warn'

          // Calculate the overall alert level for this Case
          if (alert && hasAlert !== 'warn') {
            hasAlert = alert
          }
        }
    }
      
      // Store the activity alert
      cs.ActivityAlerts.push({ name: activity.name, on: on, alert: alert })

      // Keep building the progress strings
      if (on) {
        progress.push(activity.done)
        summary = activity.done + "\n\r"
      } else if (!summaryCompleted) {
        progress.push(activity.await)
        summary += activity.await + "\n\r"
        summaryCompleted = true
      }
    }

    // Marks Case as containing an alert if any of the activities have an alert
    cs.HasAlert = hasAlert

    // Add the progress to the CaseSummary
    cs.Progress = progress
    cs.ProgressSummary = summary.trim()
}

export function SetCSDocCounts(docs: Document[], cs: CaseSummary) {
  let folderCounts: { [key: string]: number } = {}
  for (const doc of docs) {
    const topFolder = doc?.Folder?.split('/')[0] // Get the top-level folder
    const folder = topFolder ?? "Uncategorized"
    folderCounts[folder] = (folderCounts[folder] || 0) + 1
  }
  cs.Documents = {Total: docs?.length, Folders: folderCounts}
}

export function SetTreatments(treatments: any, cs: CaseSummary) {
  cs.Treatments = treatments
}

